import { z } from 'zod'
import {
  JobCreateDto,
  RecipeReference,
  RunnerCapabilitiesDto,
} from './monocleJob'

const reconstructParameters = z
  .object({
    auto_stencil: z.boolean().default(true),
    detail: z
      .enum(['preview', 'reduced', 'medium', 'full', 'raw', 'custom'])
      .default('custom'),
    max_polygon_count: z
      .number()
      .int()
      .min(1000)
      .max(2_000_000)
      .default(100_000),
  })
  .strict()
const thumbnailParameters = z
  .object({
    camera_euler_angles: z
      .array(z.number().finite())
      .length(3)
      .default([0, 0, 0]),
    camera_pan: z.array(z.number().finite()).length(3).default([0, 0, 0]),
  })
  .strict()
const simplifyParameters = z
  .object({
    triangle_budget: z.number().int().min(1000).max(1_000_000),
    texture_size: z.enum(['1K', '2K', '4K']).default('2K'),
  })
  .strict()

export const JobRecipes = {
  'model.reconstruct': {
    version: 1,
    timeout_seconds: 7200,
    creates_bundle: true,
    input_slot: 'photos',
    input_type: 'prime_source',
    tools: [
      'MonoclePrimeModelProcessor',
      'MonocleModelConverter',
      'MonoclePrimePosterRenderer',
    ],
    platform: 'darwin',
    parameters: reconstructParameters,
  },
  'thumbnail.render': {
    version: 1,
    timeout_seconds: 300,
    creates_bundle: false,
    input_slot: 'model',
    input_type: 'object_scan',
    tools: ['MonoclePrimePosterRenderer'],
    platform: 'darwin',
    parameters: thumbnailParameters,
  },
  'model.simplify': {
    version: 1,
    timeout_seconds: 1800,
    creates_bundle: true,
    input_slot: 'model',
    input_type: 'object_scan',
    tools: [
      'MonocleModelSimplifier',
      'MonocleModelConverter',
      'MonoclePrimePosterRenderer',
    ],
    platform: 'darwin',
    parameters: simplifyParameters,
  },
} as const

export type JobRecipeId = keyof typeof JobRecipes
export class JobRecipeValidationError extends Error {}
export function getJobRecipe(reference: z.infer<typeof RecipeReference>) {
  const recipe = JobRecipes[reference.id as JobRecipeId]
  if (!recipe || reference.version !== recipe.version)
    throw new JobRecipeValidationError('Unsupported recipe or version')
  return recipe
}
export function validateJobRequest(input: unknown) {
  const request = JobCreateDto.parse(input)
  const recipe = getJobRecipe(request.recipe)
  if (
    !request.inputs[recipe.input_slot] ||
    Object.keys(request.inputs).some((slot) =>
      request.recipe.id === 'model.reconstruct'
        ? !/^photos(?:_[2-8])?$/.test(slot)
        : slot !== recipe.input_slot,
    )
  )
    throw new JobRecipeValidationError(
      `Recipe requires the ${recipe.input_slot} input`,
    )
  return { ...request, parameters: recipe.parameters.parse(request.parameters) }
}
export function runnerSupportsRecipe(
  capabilities: z.infer<typeof RunnerCapabilitiesDto>,
  reference: z.infer<typeof RecipeReference>,
) {
  const recipe = getJobRecipe(reference)
  return (
    capabilities.platform === recipe.platform &&
    recipe.tools.every((tool) => !!capabilities.tools[tool]) &&
    capabilities.recipes.some(
      (candidate) =>
        candidate.id === reference.id &&
        candidate.version === reference.version,
    )
  )
}
